import React from 'react'
import { useAppState } from 'Context/AppContext/AppContext'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import {
  ButtonAction,
  Priorities,
} from 'Components/SharedComponents/Surveys/types'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { NeedsAssessmentWizardStep } from '../../constants'
import {
  HandleActionArgs,
  HandleToggleOrCreateTemplateGoal,
  TemplateLibraryMap,
} from '../../types'
import { RecommendedGoals } from '../RecommendedGoals/RecommendedGoals'
import { sendStepTransitionEvent } from '../../utils'

type WeSuggestTheseGoalsProps = {
  onAnyAction: ({ nextStep, callback }: HandleActionArgs) => void
  onSelectTemplateGoals: HandleToggleOrCreateTemplateGoal
  priorities: Priorities
  templateGoalsToCreate: number[]
  templateLibraryMap: TemplateLibraryMap
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const WeSuggestTheseGoals = ({
  onAnyAction,
  onSelectTemplateGoals,
  priorities,
  templateGoalsToCreate,
  templateLibraryMap,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WeSuggestTheseGoalsProps) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()

  const sendStepTransition = (didCreateGoals: boolean) => {
    sendStepTransitionEvent({
      currentStep: NeedsAssessmentWizardStep.WE_SUGGEST_THESE_GOALS,
      nextStep: NeedsAssessmentWizardStep.ADD_YOUR_MISSION_STATEMENT,
      userId: userConfig.id,
      nonprofitOrgId: nonprofitOrgConfig.orgId,
      funderOrgId: nonprofitOrgConfig.funderOrgId,
      userEmail: userConfig.email,
      userPrimaryFunction,
      userJourneyLength,
      userRole,
      didCreateGoals,
    })
  }

  const path = NeedsAssessmentWizardStep.ADD_YOUR_MISSION_STATEMENT
  const nextActions: ButtonAction[] = [
    {
      callback: () => {
        onSelectTemplateGoals('clear')
        sendStepTransition(false)
      },
      label: 'Ignore Goals',
      path,
      type: ActionElementType.BUTTON,
      variant: 'secondary',
    },
    {
      callback: () => {
        onSelectTemplateGoals('save')
        sendStepTransition(true)
      },
      disabled: !templateGoalsToCreate.length,
      label: 'Create Goals',
      path,
      type: ActionElementType.BUTTON,
    },
  ]

  return (
    <StepContainer>
      <Title>We suggest these goals</Title>
      <Description>
        Based on your responses, our nonprofit experts recommend the following
        goal templates to kick-start your journey. <br />
        <br />
        These goals will be created in the Goals section of Resilia. Once
        created, you will be able to edit, add tasks and assign to your team to
        track progress!
      </Description>
      <RecommendedGoals
        onSelectTemplateGoals={onSelectTemplateGoals}
        priorities={priorities}
        templateGoalsToCreate={templateGoalsToCreate}
        templateLibraryMap={templateLibraryMap}
      />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
